/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { Sidebar, Table, Input } from "@nordcloud/gnui";
import { useDisclosure } from "~/hooks";
import { mapInputStatus } from "~/tools";

type Environment = {
  name: string;
  id: string;
  nid: string;
};

type Props = {
  value: string;
  onChange: (envId: string) => void;
  placeholder?: string;
  options: Environment[];
  status?: "error" | "success";
  disabled?: boolean;
};

export function EnvironmentInput({
  value,
  onChange,
  placeholder,
  status,
  options,
  disabled = false,
}: Props) {
  const { isOpen, close, open } = useDisclosure();

  const displayValue = options.find(({ id }) => id === value)?.name ?? value;

  return (
    <>
      <Input
        disabled={disabled}
        type="text"
        value={displayValue}
        name="environment-input"
        placeholder={placeholder || "Select Environment"}
        status={mapInputStatus(status)}
        onClick={open}
      />
      <EnvironmentSelector
        isOpen={isOpen}
        options={options}
        onClose={close}
        onSubmit={(environment) => {
          onChange(environment);
          close();
        }}
      />
    </>
  );
}

type EnvironmentSelectorProps = {
  isOpen: boolean;
  onClose: () => void;
  options: Environment[];
  onSubmit: (id: string) => void;
};

function EnvironmentSelector({
  isOpen,
  onClose,
  onSubmit,
  options,
}: EnvironmentSelectorProps) {
  const [searchValue, setSearchValue] = useState("");

  const filteredOptions = options.filter((env) =>
    env.name.toLowerCase().includes(searchValue)
  );

  useEffect(() => {
    setSearchValue("");
  }, [isOpen]);

  return (
    <Sidebar isOpen={isOpen} title="Select Environment" onClick={onClose}>
      <Input
        type="text"
        name="filter"
        placeholder="Search"
        onChange={(e) => {
          setSearchValue(e.target.value.toLowerCase());
        }}
      />
      <Table small hoverline>
        <Table.thead>
          <Table.tr>
            <Table.th>Environment name</Table.th>
          </Table.tr>
        </Table.thead>
        <Table.tbody>
          {filteredOptions.map((environment) => (
            <Table.tr
              key={environment.id}
              onClick={() => onSubmit(environment.id)}
            >
              <Table.td>{environment?.name}</Table.td>
            </Table.tr>
          ))}
        </Table.tbody>
      </Table>
    </Sidebar>
  );
}
